import React, { useState, useRef, useCallback } from 'react';
import { Box, Grid } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';

import { usePosProfile } from './PosProfileContext';
import { selectCartItems, clearCart } from '../store/cartSlice';
import useKeyboardShortcuts from './useKeyboardShortcuts';
import ItemSearch from './ItemSearch';
import ItemGrid from './ItemGrid';
import CustomerSelect from './CustomerSelect';
import Cart from './Cart';
import CheckoutDialog from './CheckoutDialog';
import ReceiptDialog from './ReceiptDialog';
import ReturnsDialog from './ReturnsDialog';

/**
 * Main POS screen — two-panel layout.
 * Left: item search + item grid.
 * Right: customer picker + cart.
 * Manages checkout and receipt dialog state.
 *
 * Keyboard shortcuts:
 *   F2          — open checkout
 *   Escape      — close dialogs
 *   /           — focus search
 *   Ctrl+Backspace — clear cart
 */
export default function PosScreen() {
  const { profile, profileData } = usePosProfile();
  const dispatch = useDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [returnsOpen, setReturnsOpen] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const cartItems = useSelector(selectCartItems);
  const searchRef = useRef(null);

  // Extract config from profile data
  const posProfile = profile?.name;
  const priceList = profileData?.selling_price_list || profile?.selling_price_list;
  const itemGroup = profileData?.item_groups?.length
    ? undefined
    : 'All Item Groups';
  const defaultCustomer = profileData?.customer || profile?.customer;

  const handleCheckout = () => {
    if (cartItems.length > 0) {
      setCheckoutOpen(true);
    }
  };

  const handleCheckoutSuccess = (data) => {
    setCheckoutOpen(false);
    setReceiptData(data);
  };

  const handleReceiptClose = () => {
    setReceiptData(null);
  };

  const handleFocusSearch = useCallback(() => {
    searchRef.current?.focus();
  }, []);

  const handleClearCart = useCallback(() => {
    if (cartItems.length > 0 && window.confirm('Clear all items from the cart?')) {
      dispatch(clearCart());
    }
  }, [cartItems.length, dispatch]);

  const handleReturnSuccess = (data) => {
    setReturnsOpen(false);
    setReceiptData({
      mode: 'return',
      invoice: data.returnInvoice,
      originalInvoice: data.originalInvoice,
      items: (data.returnInvoice?.items || []).map((i) => ({
        ...i,
        qty: Math.abs(i.qty),
        amount: Math.abs((i.qty || 0) * (i.rate || 0)),
      })),
      payments: (data.returnInvoice?.payments || []).map((p) => ({
        ...p,
        amount: Math.abs(p.amount),
      })),
      subtotal: Math.abs(data.returnInvoice?.grand_total || 0),
      change: 0,
    });
  };

  const handleCloseDialog = useCallback(() => {
    if (checkoutOpen) setCheckoutOpen(false);
    if (returnsOpen) setReturnsOpen(false);
    if (receiptData) setReceiptData(null);
  }, [checkoutOpen, returnsOpen, receiptData]);

  useKeyboardShortcuts({
    onCheckout: handleCheckout,
    onCloseDialog: handleCloseDialog,
    onFocusSearch: handleFocusSearch,
    onClearCart: handleClearCart,
  });

  return (
    <Box sx={{ height: 'calc(100vh - 80px)', display: 'flex', overflow: 'hidden' }}>
      <Grid container sx={{ height: '100%' }}>
        {/* Left panel — Items */}
        <Grid
          item
          xs={12}
          md={8}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRight: { md: '1px solid' },
            borderColor: { md: 'divider' },
          }}
        >
          <ItemSearch
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            posProfile={posProfile}
            priceList={priceList}
            inputRef={searchRef}
          />
          <ItemGrid
            searchTerm={searchTerm}
            posProfile={posProfile}
            priceList={priceList}
            itemGroup={itemGroup}
          />
        </Grid>

        {/* Right panel — Cart */}
        <Grid
          item
          xs={12}
          md={4}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
          }}
        >
          <CustomerSelect defaultCustomer={defaultCustomer} />
          <Cart onCheckout={handleCheckout} onReturns={() => setReturnsOpen(true)} />
        </Grid>
      </Grid>

      {/* Checkout dialog */}
      <CheckoutDialog
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        onSuccess={handleCheckoutSuccess}
      />

      {/* Returns dialog */}
      <ReturnsDialog
        open={returnsOpen}
        onClose={() => setReturnsOpen(false)}
        onSuccess={handleReturnSuccess}
      />

      {/* Receipt dialog */}
      <ReceiptDialog
        open={!!receiptData}
        onClose={handleReceiptClose}
        receiptData={receiptData}
      />
    </Box>
  );
}
